import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import Contact from '../models/Contact';
import { COLLECTIONS } from '../platform/constants';
import { withTenantScope } from './contentWriteFields';
import {
  buildCreatedAtListQuery,
  mapAndFilterSnapshot,
} from './tenantListQuery';

export const CONTACTS_PAGE_SIZE = 20;

function contactsCollection() {
  return collection(db, COLLECTIONS.CONTACTS);
}

export function buildContactsPageQuery(user, tenantId, memberships, options = {}) {
  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.CONTACTS,
    tenantId,
    user,
    memberships,
    pageSize: options.pageSize ?? CONTACTS_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export async function fetchContactsPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildContactsPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    contacts: mapAndFilterSnapshot(
      querySnapshot,
      tenantId,
      (docSnap) => Contact.fromFirestore(docSnap),
    ),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export function subscribeContacts(user, {
  tenantId,
  memberships,
  pageSize = CONTACTS_PAGE_SIZE,
  onData,
  onError,
}) {
  if (!tenantId) {
    return () => {};
  }

  const listQuery = buildCreatedAtListQuery({
    collectionName: COLLECTIONS.CONTACTS,
    tenantId,
    user,
    memberships,
    pageSize,
  });

  return onSnapshot(
    listQuery,
    (querySnapshot) => {
      const contacts = mapAndFilterSnapshot(
        querySnapshot,
        tenantId,
        (docSnap) => Contact.fromFirestore(docSnap),
      );
      onData({
        contacts,
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      });
    },
    onError,
  );
}

export async function fetchMoreContacts(user, {
  tenantId,
  memberships,
  lastVisible,
  pageSize = CONTACTS_PAGE_SIZE,
}) {
  const listQuery = buildCreatedAtListQuery({
    collectionName: COLLECTIONS.CONTACTS,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
  const querySnapshot = await getDocs(listQuery);
  return {
    contacts: mapAndFilterSnapshot(
      querySnapshot,
      tenantId,
      (docSnap) => Contact.fromFirestore(docSnap),
    ),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export async function createContact(activeTenantId, contactData) {
  const docRef = await addDoc(
    contactsCollection(),
    withTenantScope(activeTenantId, {
      ...contactData,
      createdAt: serverTimestamp(),
    }),
  );
  return docRef.id;
}

export async function updateContactContent(contactId, {
  name,
  emails,
  phones,
  unit,
  unitName,
  listGroupId,
}) {
  await updateDoc(doc(db, COLLECTIONS.CONTACTS, contactId), {
    name,
    emails,
    phones,
    unit,
    unitName,
    listGroupId: listGroupId || '',
  });
}

export async function deleteContact(contactId) {
  await deleteDoc(doc(db, COLLECTIONS.CONTACTS, contactId));
}
